import { IAdvisorChoice, IAdvisorEntry } from "@types";

/**
 * The advisor API has no "look up session by offer" endpoint — only create / choose / get-by-
 * session-id — so the offer -> session mapping only exists if we remember it ourselves. Kept in
 * sessionStorage (not localStorage/Redux) to mirror the reference implementation's own
 * lifetime: survives a refresh, clears when the tab closes.
 */

const SESSION_KEY_PREFIX = "toa.advisor.session";
const TRANSCRIPT_KEY_PREFIX = "toa.advisor.transcript";

// Keyed by company + offer, not offer alone — offer ids are already expected to be globally
// unique, but this makes the isolation explicit rather than relying on that assumption holding.
const sessionKey = (companyId: number | string, offerId: number | string) =>
  `${SESSION_KEY_PREFIX}.${companyId}.${offerId}`;
const transcriptKey = (sessionId: string) =>
  `${TRANSCRIPT_KEY_PREFIX}.${sessionId}`;

export function loadCachedSessionId(
  companyId: number | string,
  offerId: number | string
): string | null {
  return sessionStorage.getItem(sessionKey(companyId, offerId));
}

export function saveCachedSessionId(
  companyId: number | string,
  offerId: number | string,
  sessionId: string
): void {
  sessionStorage.setItem(sessionKey(companyId, offerId), sessionId);
}

export function clearCachedSessionId(
  companyId: number | string,
  offerId: number | string
): void {
  sessionStorage.removeItem(sessionKey(companyId, offerId));
}

interface ICachedTranscript {
  entries: IAdvisorEntry[];
  choices: IAdvisorChoice[];
}

export function loadCachedTranscript(
  sessionId: string
): ICachedTranscript | null {
  const raw = sessionStorage.getItem(transcriptKey(sessionId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ICachedTranscript;
    return parsed?.entries?.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function saveCachedTranscript(
  sessionId: string,
  entries: IAdvisorEntry[],
  choices: IAdvisorChoice[]
): void {
  sessionStorage.setItem(
    transcriptKey(sessionId),
    JSON.stringify({ entries, choices })
  );
}

export function clearCachedTranscript(sessionId: string): void {
  sessionStorage.removeItem(transcriptKey(sessionId));
}
